import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SparklesIcon, XMarkIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { api } from '../utils/api';
import { Spinner } from './UI';

// ── Assistant reply renderer ──────────────────────────────────────────────
// The model sometimes answers in Markdown. We render a small, safe subset so the reply
// reads naturally: "# Heading" → bold line (no raw #), **bold**, "- item" / "1. item" lists.
function inline(text, keyBase) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (/^\*\*[^*]+\*\*$/.test(p)
    ? <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>
    : <React.Fragment key={`${keyBase}-${i}`}>{p.replace(/(^|\s)[*_](\S[^*_]*\S|\S)[*_](?=\s|$|[.,;:!?])/g, '$1$2')}</React.Fragment>));
}

export function AssistantText({ text }) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const h = line.match(/^\s*#{1,6}\s+(.*)$/);
    const ul = line.match(/^\s*[-*•]\s+(.*)$/);
    const ol = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    const isRow = /^\s*\|.*\|\s*$/.test(line);
    if (isRow) {
      // Markdown table row; the |---|---| separator row is skipped.
      if (/^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-')) return;
      const cells = line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
      if (!list || list.type !== 'table') { flush(); list = { type: 'table', rows: [], key: i }; }
      list.rows.push(cells);
      return;
    }
    if (h) { flush(); blocks.push({ type: 'h', text: h[1].replace(/\*\*/g, ''), key: i }); }
    else if (ul) { if (!list || list.type !== 'ul') { flush(); list = { type: 'ul', items: [], key: i }; } list.items.push(ul[1]); }
    else if (ol) { if (!list || list.type !== 'ol') { flush(); list = { type: 'ol', items: [], key: i }; } list.items.push(ol[2]); }
    else if (!line.trim() || /^\s*(-{3,}|\*{3,})\s*$/.test(line)) { flush(); }
    else { flush(); blocks.push({ type: 'p', text: line, key: i }); }
  });
  flush();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {blocks.map(b => {
        if (b.type === 'h') return <div key={b.key} style={{ fontWeight: 700, marginTop: 4 }}>{inline(b.text, b.key)}</div>;
        if (b.type === 'p') return <div key={b.key}>{inline(b.text, b.key)}</div>;
        if (b.type === 'table') return (
          <div key={b.key} style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.92em', width: '100%' }}>
              <tbody>
                {b.rows.map((r, ri) => (
                  <tr key={ri}>
                    {r.map((c, ci) => {
                      const Cell = ri === 0 && b.rows.length > 1 ? 'th' : 'td';
                      return <Cell key={ci} style={{ textAlign: 'left', padding: '3px 8px 3px 0', borderBottom: '1px solid rgba(0,0,0,0.08)', fontWeight: Cell === 'th' ? 700 : 400, verticalAlign: 'top' }}>{inline(c, `${b.key}-${ri}-${ci}`)}</Cell>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        const Tag = b.type === 'ul' ? 'ul' : 'ol';
        return (
          <Tag key={b.key} style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {b.items.map((it, j) => <li key={j}>{inline(it, `${b.key}-${j}`)}</li>)}
          </Tag>
        );
      })}
    </div>
  );
}

// ── Floating assistant (always visible, bottom-right) ─────────────────────
// A compact chat on top of the same conversations as the full /assistant page.
// Read-only like the page: it only answers and suggests, never changes data.
export default function AssistantDock() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  const loadLatest = useCallback(() => {
    api.ai.assistantConversations().then(d => {
      const first = (d.conversations || [])[0];
      if (!first) { setLoaded(true); return; }
      setConvId(first.id);
      return api.ai.assistantMessages(first.id).then(m => setMessages(m.messages || []));
    }).catch(() => { }).finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!open || status) return;
    api.ai.assistantStatus().then(d => {
      setStatus(d.status);
      if (d.status === 'granted') loadLatest();
    }).catch(() => setStatus('none'));
  }, [open, status, loadLatest]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, thinking, open]);

  // Other parts of the app can open the assistant with a suggested question
  // (e.g. the customer workspace's Companion card): window.dispatchEvent(new CustomEvent('hb-assistant-open', { detail: { prompt } })).
  useEffect(() => {
    const onOpen = (e) => { setOpen(true); if (e.detail?.prompt) setInput(e.detail.prompt); };
    window.addEventListener('hb-assistant-open', onOpen);
    return () => window.removeEventListener('hb-assistant-open', onOpen);
  }, []);

  // The full assistant page already is the chat — no floating copy on top of it.
  if (location.pathname.startsWith('/assistant')) return null;

  async function send() {
    const q = input.trim();
    if (!q || thinking) return;
    setMessages(m => [...m, { role: 'user', content: q }]);
    setInput(''); setThinking(true);
    try {
      const r = await api.ai.assistantChat(q, convId);
      setMessages(m => [...m, { role: 'assistant', content: r.answer }]);
      if (r.conversationId) setConvId(r.conversationId);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: `Sorry — I couldn’t answer that just now (${e.message}).` }]);
    } finally { setThinking(false); }
  }
  function newChat() { setConvId(null); setMessages([]); setInput(''); }

  return (
    <>
      {open && (
        <div style={{
          position: 'fixed', right: 20, bottom: 84, zIndex: 120,
          width: 'min(380px, calc(100vw - 32px))', height: 'min(560px, calc(100vh - 120px))',
          background: 'var(--canvas-white)', borderRadius: 'var(--radius-lg)',
          boxShadow: '0 18px 50px rgba(0,0,0,0.22)', border: '1px solid var(--line)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', background: 'var(--ink)', color: '#fff' }}>
            <SparklesIcon style={{ width: 18, height: 18 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.98rem' }}>HB Assistant</div>
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.55)' }}>reads Basecamp · suggests · never edits</div>
            </div>
            {status === 'granted' && <button onClick={newChat} title="New chat" style={dockIconBtn}>+ New</button>}
            <Link to="/assistant" title="Open full assistant" style={{ ...dockIconBtn, display: 'flex' }}><ArrowsPointingOutIcon style={{ width: 16, height: 16 }} /></Link>
            <button onClick={() => setOpen(false)} title="Close" style={{ ...dockIconBtn, display: 'flex' }}><XMarkIcon style={{ width: 18, height: 18 }} /></button>
          </div>

          {status === null ? (
            <div style={{ margin: 'auto' }}><Spinner /></div>
          ) : status !== 'granted' ? (
            <div style={{ padding: 18, fontSize: '0.88rem', color: 'var(--ink-soft)', lineHeight: 1.55 }}>
              The HB Assistant reads what’s in Basecamp — your projects, the method, profiles and Units — and suggests what to do next. It never changes anything.
              <div style={{ marginTop: 12 }}>
                {status === 'requested'
                  ? 'Your access request is waiting for an administrator.'
                  : <Link to="/assistant" style={{ color: 'var(--ink)', textDecoration: 'underline' }}>Request access →</Link>}
              </div>
            </div>
          ) : (
            <>
              <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {!loaded ? <div style={{ margin: 'auto' }}><Spinner /></div> : messages.length === 0 ? (
                  <div style={{ color: 'var(--ink-faint)', fontSize: '0.85rem', margin: 'auto', textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}>
                    Hi! Ask me about your customers, the method, or what to do next — e.g. <i>“what’s my next step on Acme?”</i>
                  </div>
                ) : messages.map((m, i) => (
                  <div key={i} style={{
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%',
                    background: m.role === 'user' ? 'var(--ink)' : 'var(--canvas-warm)',
                    color: m.role === 'user' ? '#fff' : 'var(--ink)',
                    padding: '9px 12px', borderRadius: 'var(--radius-md)', fontSize: '0.86rem', lineHeight: 1.5,
                    whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal',
                  }}>
                    {m.role === 'user' ? m.content : <AssistantText text={m.content} />}
                  </div>
                ))}
                {thinking && <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-soft)', fontSize: '0.82rem' }}><Spinner size={14} /> thinking…</div>}
              </div>
              <div style={{ borderTop: '1px solid var(--line)', padding: 10, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask the HB Assistant…" rows={2}
                  style={{ flex: 1, resize: 'none', fontFamily: 'var(--font-body)', fontSize: '0.86rem', padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)' }} />
                <button onClick={send} disabled={!input.trim() || thinking} style={{
                  padding: '9px 14px', borderRadius: 'var(--radius-md)', border: 'none', cursor: input.trim() && !thinking ? 'pointer' : 'default',
                  background: 'var(--ink)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: '0.84rem', opacity: input.trim() && !thinking ? 1 : 0.5,
                }}>Send</button>
              </div>
            </>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close HB Assistant' : 'Open HB Assistant'}
        title="HB Assistant"
        style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 120,
          height: 52, borderRadius: 26, padding: open ? 0 : '0 18px 0 14px', width: open ? 52 : 'auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: 'var(--ink)', color: '#fff', border: 'none', cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)', fontFamily: 'var(--font-body)', fontSize: '0.86rem', fontWeight: 600,
        }}
      >
        {open ? <XMarkIcon style={{ width: 22, height: 22 }} /> : <><SparklesIcon style={{ width: 20, height: 20 }} /> Ask HB Assistant</>}
      </button>
    </>
  );
}

const dockIconBtn = { background: 'none', border: 'none', color: 'rgba(255,255,255,0.75)', cursor: 'pointer', padding: 4, fontSize: '0.74rem', fontFamily: 'var(--font-body)', textDecoration: 'none', alignItems: 'center' };
